/**
 * Table-client backend for consumer-local regular-table columns (ADR 0020, AC-SOURCE-002).
 *
 * Plugs into `TableColumnProvider` from ui-keyed-columns. Each request (re)creates the backend
 * table definition for a key and field set. The web app re-posts the whole definition with a
 * stable `userViewKey`; the server either amends the existing view in place and answers with the
 * same table key, or answers with a new key (CN-IMPL-004). This backend follows TableModel's
 * same-key rule: an answer with the current table key keeps the existing subscription, and only a
 * new key opens a new subscription and closes the old one.
 *
 * Values are current only while the subscription that produced them stays open (OD-RETAIN-001).
 * When a new key replaces the subscription, every symbol the old one reported is removed before
 * the switch, so its values show as unavailable until the new table reports them.
 *
 * Creation requests are supersedable: a newer request aborts the pending one, and a result that
 * arrives after its request was superseded or the stream disposed is dropped (AC-LIFE-002).
 * Rows are borrowed table-client rows read synchronously; only their keys are retained, so diff
 * deletions can be reported by key.
 */
import { RowField, RowIdField } from '@thinkalpha/table-client';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
/**
 * Window size for keyed-table subscriptions (ADR 0020, OD-BOUNDS-001). An amended table keeps its
 * subscription and its bounds, and a keyed table holds only the requested symbols, so a fixed window
 * above any keys window covers every row it can have without a resubscribe.
 */
export const KEYED_TABLE_WINDOW_SIZE = 1000;
function isPromiseLike(value) {
    return 'then' in value && typeof value.then === 'function';
}
/** One pending creation request; disposing it supersedes the request. */
class PendingRequest {
    #abort = new AbortController();
    get signal() {
        return this.#abort.signal;
    }
    [Symbol.dispose]() {
        this.#abort.abort();
    }
}
/** The subscription currently serving a stream, with the row keys it has reported. */
class AdoptedTable {
    tableKey;
    /** Row identity → key, so diff deletions are reported by key. */
    rowKeys = new Map();
    #lifetime = new DisposableStack();
    constructor(tableKey, subscription, receive) {
        this.tableKey = tableKey;
        this.#lifetime.use(subscription);
        const rows = subscription.updates$.subscribe((batch) => receive(this, batch));
        this.#lifetime.defer(() => rows.unsubscribe());
    }
    [Symbol.dispose]() {
        this.#lifetime.dispose();
    }
}
class TableClientRowStream {
    #backend;
    #options;
    #listener;
    #pending = new DisposableBox();
    #table = new DisposableBox();
    #lifetime = new DisposableStack();
    #fields = [];
    constructor(backend, options, listener) {
        this.#backend = backend;
        this.#options = options;
        this.#listener = listener;
        this.#lifetime.use(this.#table);
        this.#lifetime.use(this.#pending);
    }
    request(request) {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#fields = request.fields;
        this.#backend.tablesRequested += 1;
        const pending = new PendingRequest();
        this.#pending.next(pending);
        const adopt = (created) => {
            if (pending.signal.aborted) {
                this.#backend.lateTablesReleased += 1;
                return;
            }
            if (this.#table.current?.tableKey === created.tableKey) {
                // Amended in place: the existing subscription keeps serving the table.
                this.#backend.amendedInPlace += 1;
                return;
            }
            this.#backend.tablesSubscribed += 1;
            this.#releaseReported();
            this.#table.next(() => new AdoptedTable(created.tableKey, created.subscribe(), (table, batch) => this.#receive(table, batch)));
        };
        const created = this.#options.createTable(request, pending.signal);
        if (isPromiseLike(created)) {
            created.then(adopt, (error) => {
                if (!pending.signal.aborted) {
                    this.#options.onCreateError?.(error, request);
                }
            });
        }
        else {
            adopt(created);
        }
    }
    [Symbol.dispose]() {
        this.#lifetime.dispose();
    }
    /** Removes every symbol the current subscription reported: its values stop being live. */
    #releaseReported() {
        const table = this.#table.current;
        if (table === undefined || table.rowKeys.size === 0) {
            return;
        }
        this.#listener.begin();
        for (const key of new Set(table.rowKeys.values())) {
            this.#listener.removed(key);
        }
        this.#listener.end();
    }
    #receive(table, batch) {
        this.#listener.begin();
        if (batch.type === 'diff') {
            for (const rowId of batch.deletedRowIds) {
                const key = table.rowKeys.get(rowId);
                table.rowKeys.delete(rowId);
                if (key !== undefined) {
                    this.#listener.removed(key);
                }
            }
        }
        for (const row of batch) {
            const key = this.#options.keyOf(row);
            if (key === undefined) {
                continue;
            }
            table.rowKeys.set(row[RowIdField] ?? row[RowField], key);
            this.#listener.row(key, this.#values(row));
        }
        this.#listener.end();
    }
    *#values(row) {
        for (const field of this.#fields) {
            if (Object.hasOwn(row, field)) {
                yield [field, row[field]];
            }
        }
    }
}
export class TableClientRowBackend {
    #options;
    /** Creation requests sent (one per provider request). */
    tablesRequested = 0;
    /** Answers that returned the current table key: amended in place, no new subscription. */
    amendedInPlace = 0;
    /** Answers with a new table key: a new subscription replaced the previous one. */
    tablesSubscribed = 0;
    /** Answers that arrived after their request was superseded or the stream disposed. */
    lateTablesReleased = 0;
    constructor(options) {
        this.#options = options;
    }
    open(listener) {
        return new TableClientRowStream(this, this.#options, listener);
    }
}
//# sourceMappingURL=tableClientRowBackend.js.map